import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';

type AppRole = 'admin' | 'coach' | 'client' | 'support';

export function useUserRole() {
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  // Derived convenience values
  const isAdmin = roles.includes('admin');
  const isCoach = roles.includes('coach');
  const isClient = roles.includes('client');
  const isSupport = roles.includes('support');
  const isCoachOrAdmin = isCoach || isAdmin;
  // canHandleSupport: usuarios autorizados para atender tickets.
  // Admins implícitamente pueden (god mode). Support es el rol
  // dedicado para esto sin tener que dar admin completo.
  const canHandleSupport = isAdmin || isSupport;
  // Primary role for display: admin > support > coach > client
  const role: AppRole | null =
    isAdmin   ? 'admin'
    : isSupport ? 'support'
    : isCoach ? 'coach'
    : isClient ? 'client'
    : (roles[0] ?? null);

  const fetchUserRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (error) {
        console.error('Error fetching user roles:', error);
        setRoles([]);
      } else if (data && data.length > 0) {
        setRoles(data.map(d => d.role as AppRole));
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error('Error in fetchUserRoles:', error);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        fetchUserRoles(currentUser.id);
      } else {
        setRoles([]);
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        fetchUserRoles(currentUser.id);
      } else {
        setRoles([]);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return {
    user, role, roles, loading,
    isAdmin, isCoach, isClient, isSupport,
    isCoachOrAdmin, canHandleSupport,
  };
}
